/**
 * Testimonial wall.
 *
 * The rows are not scrollers, and the lane is not a long strip.
 *
 * Two earlier versions leaned on the row's own `scrollLeft`: drift by adding
 * to it, wrap by subtracting a whole track width when it ran far enough. That
 * is sound in principle and unreliable in practice on a phone. iOS runs
 * momentum scrolling on a separate thread, so a programmatic write during a
 * flick is quietly dropped or fought over — which is how the row could sail
 * past the wrap point and show white. There is no way to ask politely.
 *
 * So the row owns no scroll position at all. It clips, and a lane inside it
 * is moved by `transform`. Dragging is handled here too, with its own
 * inertia, so a finger and the drift write to the same number and the
 * browser is never involved.
 *
 * The lane holds only a little more than one screenful, and the leading card
 * is moved to the back the moment it passes out of sight. A first attempt
 * laid out several whole copies of the list instead and slid the lot; that
 * is simpler, but it builds a composited layer thousands of pixels wide, and
 * Safari on iOS will not paint all of one — which is what the empty stretches
 * were. Recycling keeps the lane barely wider than the row, so there is no
 * oversized layer to fail, and the offset never grows beyond a single card.
 */
class RanaWall extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('[data-wall-grid]'));
    if (!this.rows.length) return;

    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.visible = true;
    this.frame = this.frame.bind(this);

    this.rows.forEach((row, index) => this.#setup(row, index % 2 === 0 ? 1 : -1));

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
      }, { rootMargin: '120px' });
      this.io.observe(this);
    }

    if ('ResizeObserver' in window) {
      this.width = this.clientWidth;

      this.ro = new ResizeObserver(() => {
        // A phone fires this constantly while the page scrolls — the address
        // bar slides away and everything on the page is resized. Only a
        // change in width can affect how many copies a row needs.
        const width = this.clientWidth;
        if (width === this.width) return;
        this.width = width;

        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.#remeasure(), 180);
      });
      this.ro.observe(this);
    }

    this.last = 0;
    this.raf = requestAnimationFrame(this.frame);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    window.clearTimeout(this.resizeTimer);
    this.io?.disconnect();
    this.ro?.disconnect();
  }

  #setup(row, direction) {
    const track = row.querySelector('[data-wall-track]');
    if (!track) return;

    // The lane is the original list, kept for its semantics; the row simply
    // stops being a scroller. A row whose script never runs is left as a
    // plain, readable, scrollable strip.
    row.classList.add('is-live');

    row.wall = {
      lane: track,
      seed: Array.from(track.children),
      direction,
      offset: 0,
      dragging: false,
      pointer: null,
      lastX: 0,
      lastTime: 0,
      velocity: 0,
      hover: false,
      gap: 0,
      speed: parseFloat(getComputedStyle(this).getPropertyValue('--rana-wall-speed')) || 80,
    };

    if (!row.wall.seed.length) return;

    row.addEventListener('pointerdown', (event) => this.#down(row, event));
    row.addEventListener('pointermove', (event) => this.#move(row, event));
    for (const name of ['pointerup', 'pointercancel']) {
      row.addEventListener(name, (event) => this.#up(row, event));
    }
    row.addEventListener('mouseenter', () => { row.wall.hover = true; }, { passive: true });
    row.addEventListener('mouseleave', () => { row.wall.hover = false; }, { passive: true });
    row.addEventListener('dragstart', (event) => event.preventDefault());

    this.#measure(row);
  }

  #measure(row) {
    const state = row.wall;
    if (!state) return;

    state.gap = parseFloat(getComputedStyle(state.lane).columnGap) || 0;
    if (!state.seed[0] || !state.seed[0].getBoundingClientRect().width) return;

    this.#fill(row);
    state.ready = true;
    this.#draw(row);
  }

  /**
   * Tops the lane up to a screenful plus two cards, cloning from the original
   * set in order. Never removes: a card that is already there is already
   * paid for, and taking one out mid-drift is how a gap appears.
   */
  #fill(row) {
    const state = row.wall;
    if (!state) return;

    const target = row.clientWidth + this.#widest(row) * 2 + state.gap * 2;
    let guard = 200;

    while (this.#laneWidth(row) < target && guard > 0) {
      guard -= 1;
      const source = state.seed[state.lane.children.length % state.seed.length];
      const clone = source.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      for (const node of clone.querySelectorAll('[id]')) node.removeAttribute('id');
      // the theme editor must not think a clone is a real block
      clone.removeAttribute('data-shopify-editor-block');
      for (const node of clone.querySelectorAll('[data-shopify-editor-block]')) {
        node.removeAttribute('data-shopify-editor-block');
      }
      state.lane.appendChild(clone);
    }
  }

  #laneWidth(row) {
    const state = row.wall;
    let total = 0;
    for (const card of state.lane.children) {
      total += card.getBoundingClientRect().width + state.gap;
    }
    return total;
  }

  #widest(row) {
    const state = row.wall;
    let widest = 0;
    for (const card of state.seed) {
      widest = Math.max(widest, card.getBoundingClientRect().width);
    }
    return widest;
  }

  #remeasure() {
    for (const row of this.rows) {
      if (!row.wall) continue;
      row.wall.gap = parseFloat(getComputedStyle(row.wall.lane).columnGap) || 0;
      this.#fill(row);
      this.#draw(row);
    }
  }

  #down(row, event) {
    const state = row.wall;
    if (!state || !state.ready || event.button > 0) return;

    state.dragging = true;
    state.pointer = event.pointerId;
    state.lastX = event.clientX;
    state.lastTime = event.timeStamp;
    state.velocity = 0;
    row.classList.add('is-held');

    try {
      row.setPointerCapture(event.pointerId);
    } catch (error) {
      /* capture is a nicety; the move handler works without it */
    }
  }

  #move(row, event) {
    const state = row.wall;
    if (!state || !state.dragging || event.pointerId !== state.pointer) return;

    const dx = event.clientX - state.lastX;
    const dt = event.timeStamp - state.lastTime;

    state.offset -= dx;
    if (dt > 0) state.velocity = -dx / dt;

    state.lastX = event.clientX;
    state.lastTime = event.timeStamp;

    this.#draw(row);
  }

  #up(row, event) {
    const state = row.wall;
    if (!state || !state.dragging || event.pointerId !== state.pointer) return;

    state.dragging = false;
    state.pointer = null;
    row.classList.remove('is-held');

    // A flick keeps travelling and eases out, rather than stopping dead
    // under the finger.
    state.velocity = Math.max(-4, Math.min(4, state.velocity));
  }

  /**
   * Recycles whichever card has left the view, then paints.
   *
   * Travelling forwards, the first card is moved to the back once the offset
   * has passed it, and that width comes straight back off the offset — so
   * the two changes cancel and nothing moves on screen. Backwards, the last
   * card comes round to the front. The offset therefore stays inside the
   * width of one card, whatever a fling does to it, and there is no end to
   * reach.
   */
  #draw(row) {
    const state = row.wall;
    if (!state || !state.ready) return;

    const lane = state.lane;
    let guard = 100;

    while (guard > 0 && lane.children.length > 1) {
      guard -= 1;

      const first = lane.firstElementChild;
      const firstWidth = first.getBoundingClientRect().width + state.gap;

      if (state.offset >= firstWidth) {
        lane.appendChild(first);
        state.offset -= firstWidth;
        continue;
      }

      if (state.offset < 0) {
        const last = lane.lastElementChild;
        lane.insertBefore(last, first);
        state.offset += last.getBoundingClientRect().width + state.gap;
        continue;
      }

      break;
    }

    lane.style.transform = `translateX(${-state.offset}px)`;
  }

  #seedWidth(row) {
    const state = row.wall;
    if (state.seedWidth) return state.seedWidth;

    let total = 0;
    for (const card of state.seed) total += card.getBoundingClientRect().width + state.gap;
    state.seedWidth = total || 1000;
    return state.seedWidth;
  }

  frame(now) {
    const elapsed = this.last ? Math.min(64, now - this.last) : 0;
    this.last = now;

    for (const row of this.rows) {
      const state = row.wall;
      if (!state) continue;

      if (!state.ready) {
        this.#measure(row);
        continue;
      }

      if (!elapsed) continue;

      if (state.dragging) continue;

      if (Math.abs(state.velocity) > 0.02) {
        // inertia from the flick, decaying away
        state.offset += state.velocity * elapsed;
        state.velocity *= Math.pow(0.94, elapsed / 16.67);
        this.#draw(row);
        continue;
      }

      state.velocity = 0;

      if (!this.visible || this.motion.matches || state.hover) continue;

      // one full pass of the original set per `speed` seconds
      state.offset += state.direction * (this.#seedWidth(row) / state.speed) * (elapsed / 1000);
      this.#draw(row);
    }

    this.raf = requestAnimationFrame(this.frame);
  }
}

if (!customElements.get('rana-wall')) {
  customElements.define('rana-wall', RanaWall);
}
